import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Target, TrendingUp, Clock, AlertCircle } from 'lucide-react';
import ProductSelector from '../components/SmartPublish/ProductSelector';
import PlatformPreviews from '../components/SmartPublish/PlatformPreviews';
import AIScoreCard from '../components/SmartPublish/AIScoreCard';
import ContentEditor from '../components/SmartPublish/ContentEditor';
import ActionsSuggestions from '../components/SmartActions/ActionsSuggestions';

const SmartPublishStudio = () => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState('instagram');
  const [generatedContent, setGeneratedContent] = useState('');
  const [aiScore, setAiScore] = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const platforms = [
    { id: 'instagram', name: 'Instagram', color: '#E4405F', icon: '📸' },
    { id: 'facebook', name: 'Facebook', color: '#1877F2', icon: '👥' },
    { id: 'linkedin', name: 'LinkedIn', color: '#0A66C2', icon: '💼' },
    { id: 'x', name: 'X (Twitter)', color: '#000000', icon: '✖️' },
    { id: 'tiktok', name: 'TikTok', color: '#000000', icon: '🎵' },
    { id: 'youtube', name: 'YouTube', color: '#FF0000', icon: '▶️' }
  ];

  const handleProductSelect = async (product) => {
    setSelectedProduct(product);
    setIsAnalyzing(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/intelligence/c8/smart-publish/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          product_data: product
        })
      });

      const result = await response.json();
      if (result.success) {
        setAnalysisResult(result.data);
      }
    } catch (error) {
      console.error('Analysis error:', error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedProduct) return;

    setIsGenerating(true);
    setGeneratedContent('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/intelligence/c8/smart-publish/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          product_data: selectedProduct,
          platform: selectedPlatform,
          language: 'fr'
        })
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const content = line.slice(6);
            if (content !== '[ERROR]') {
              setGeneratedContent(prev => prev + content);
            }
          }
        }
      }
    } catch (error) {
      console.error('Generation error:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  useEffect(() => {
    if (generatedContent && generatedContent.length > 50) {
      calculateScore();
    }
  }, [generatedContent]);

  const calculateScore = async () => {
    try {
      const hashtags = extractHashtags(generatedContent);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/intelligence/c8/smart-publish/score`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          content: generatedContent,
          platform: selectedPlatform,
          hashtags
        })
      });

      const result = await response.json();
      if (result.success) {
        setAiScore(result.data);
        predictPerformance(hashtags);
      }
    } catch (error) {
      console.error('Scoring error:', error);
    }
  };

  const predictPerformance = async (hashtags) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/intelligence/c8/smart-publish/predict`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          content: generatedContent,
          platform: selectedPlatform,
          hashtags
        })
      });

      const result = await response.json();
      if (result.success) {
        setPrediction(result.data);
      }
    } catch (error) {
      console.error('Prediction error:', error);
    }
  };

  const extractHashtags = (content) => {
    const hashtagRegex = /#[a-zA-Z0-9_]+/g;
    const matches = content.match(hashtagRegex);
    return matches ? matches.map(tag => tag.slice(1)) : [];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <div className="border-b border-white/5 backdrop-blur-xl bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Sparkles className="inline mr-3 text-cyan-400" size={36} />
                Smart Publish Studio
              </h1>
              <p className="text-slate-400 text-lg">Génération de contenu IA optimisée pour chaque plateforme</p>
            </div>
            <div className="flex gap-3">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="px-6 py-3 bg-cyan-500/10 text-cyan-400 rounded-xl border border-cyan-500/30 hover:bg-cyan-500/20 transition-all duration-200"
                data-testid="help-button"
              >
                Aide
              </motion.button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-8 py-8">
        <div className="grid grid-cols-12 gap-6">
          {/* Left Column - Product & Platforms */}
          <div className="col-span-4 space-y-6">
            {/* Product Selector */}
            <ProductSelector
              selectedProduct={selectedProduct}
              onProductSelect={handleProductSelect}
              isAnalyzing={isAnalyzing}
            />

            {/* Platform Selector */}
            {selectedProduct && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6"
              >
                <h3 className="text-white font-semibold text-lg mb-4 flex items-center gap-2">
                  <Target size={20} className="text-cyan-400" />
                  Plateformes cibles
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {platforms.map(platform => (
                    <motion.button
                      key={platform.id}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setSelectedPlatform(platform.id)}
                      className={`p-4 rounded-xl border transition-all duration-200 ${
                        selectedPlatform === platform.id
                          ? 'bg-cyan-500/20 border-cyan-400 shadow-lg shadow-cyan-500/20'
                          : 'bg-white/5 border-white/10 hover:bg-white/10'
                      }`}
                      data-testid={`platform-${platform.id}`}
                    >
                      <div className="text-2xl mb-2">{platform.icon}</div>
                      <div className="text-white text-sm font-medium">{platform.name}</div>
                      {analysisResult && (
                        <div className="text-xs text-cyan-400 mt-1">
                          {Math.round(analysisResult.platform_suitability[platform.id] * 100)}% adapté
                        </div>
                      )}
                    </motion.button>
                  ))}
                </div>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleGenerate}
                  disabled={isGenerating || !selectedProduct}
                  className="w-full mt-6 px-6 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-cyan-500/30 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="generate-button"
                >
                  {isGenerating ? '✨ Génération en cours...' : '✨ Générer le contenu'}
                </motion.button>
              </motion.div>
            )}

            {/* Analysis Result */}
            {analysisResult && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6"
              >
                <h3 className="text-white font-semibold text-lg mb-4">Analyse IA</h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <span className="text-slate-400">Catégorie:</span>
                    <span className="text-white ml-2">{analysisResult.category}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Audience:</span>
                    <span className="text-white ml-2">{analysisResult.target_audience}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Ton recommandé:</span>
                    <span className="text-white ml-2">{analysisResult.tone_recommendation}</span>
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          {/* Center Column - Content Editor */}
          <div className="col-span-5 space-y-6">
            <ContentEditor
              content={generatedContent}
              onContentChange={setGeneratedContent}
              platform={selectedPlatform}
              isGenerating={isGenerating}
            />

            {/* Prediction Card */}
            {prediction && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 backdrop-blur-xl rounded-2xl border border-purple-500/20 p-6"
              >
                <h3 className="text-white font-semibold text-lg mb-4 flex items-center gap-2">
                  <TrendingUp size={20} className="text-purple-400" />
                  Prédiction de performance
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/5 rounded-xl p-4">
                    <div className="text-slate-400 text-sm mb-1">Portée estimée</div>
                    <div className="text-2xl font-bold text-white">{prediction.predicted_reach.toLocaleString()}</div>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4">
                    <div className="text-slate-400 text-sm mb-1">Engagement</div>
                    <div className="text-2xl font-bold text-purple-400">{(prediction.predicted_engagement * 100).toFixed(1)}%</div>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2 text-sm text-slate-300">
                  <Clock size={16} className="text-cyan-400" />
                  Meilleur horaire: {new Date(prediction.best_time).toLocaleString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                </div>
              </motion.div>
            )}
          </div>

          {/* Right Column - AI Score & Actions */}
          <div className="col-span-3 space-y-6">
            {aiScore && (
              <AIScoreCard
                score={aiScore}
                onOptimize={() => {}}
              />
            )}

            <ActionsSuggestions currentPage="smart-publish" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmartPublishStudio;
